import { FlensSlider } from './slider';
import './slider-slide';

/**
 * A slider using the scroll snap feature that can be synced with another slider,
 * by setting the sync attribute to the ID of the slider to sync with.
 * The synced slider must have the same amount of slides.
 */
export class FlensSynchronizingSlider extends FlensSlider {
	static properties = {
		sync: { type: String },
	};

	constructor() {
		super();
		this._onSyncedChanging = this._onSyncedChanging.bind(this);
		this._onSyncedScrollEnd = this._onSyncedScrollEnd.bind(this);

		this.sync = '';

		this._syncedSlider = null;
		this._syncPaused = false;
		this._syncedStartedScroll = false;
		this._syncedSettling = false;
		this._settlingFromSynced = false;
		this._isSynced = false;
	}

	moveToSlide(slide, immediate = false) {
		if (!slide) {
			console.error('Missing slide parameter', slide);
			return;
		}
		super.moveToSlide(slide, immediate);
		if (this._syncedSlider) {
			this.pauseSync(500);
			this._syncedSlider.moveToIndex(this.slides.indexOf(slide), immediate);
		}
	}

	/**
	 * Move in the given direction.
	 * @param {number} direction The number of slides to move, can be negative to
	 *   move to previous slides.
	 * @param {boolean} [immediate] Whether to move to the target position
	 *   immediately or smoothly scroll.
	 */
	move(direction, immediate = false) {
		if (direction === 0) {
			return;
		}
		super.move(direction, immediate);
		if (this._syncedSlider) {
			this.pauseSync(500);
			this._syncedSlider.move(direction, immediate);
		}
	}

	pauseSync(timeout = 500) {
		if (!this._syncedSlider) {
			return; // Nothing to pause
		}
		clearTimeout(this._syncPauseTimer);
		this._syncPaused = true;
		this._syncPauseTimer = setTimeout(() => this._checkSyncPauseEnd(), timeout);
	}

	/** @private */
	_checkSyncPauseEnd() {
		if (this.isScrolling || this._syncedSlider?.isScrolling) {
			this._syncPauseTimer = setTimeout(() => this._checkSyncPauseEnd(), 100);
		} else {
			this._syncPauseTimer = 0;
			this._syncPaused = false;

			// Fix now possibly different scroll positions
			if (this._syncedSlider?.isScrolling && !this._syncedStartedScroll) {
				// Ignore synced slider and wait until it's scroll snap settles
				this._syncedSettling = true;
			}
			this._manualSync();
		}
	}

	/** @private */
	_updateSyncedSlider() {
		const slider = document.getElementById(this.sync);
		if (slider === this._syncedSlider) {
			return; // No change
		}
		if (this._syncedSlider) {
			this._syncedSlider._isSynced = false;
			this._syncedSlider.removeEventListener(
				'changing',
				this._onSyncedChanging,
			);
			this._syncedSlider.removeEventListener(
				'changed',
				this._onSyncedScrollEnd,
			);
		}
		clearTimeout(this._syncPauseTimer);
		this._syncPaused = false;
		this._syncedSettling = false;

		this._syncedSlider = slider;
		if (slider) {
			slider._isSynced = true;
			slider.updateComplete?.then(() => this._manualSync());
			slider.addEventListener('changing', this._onSyncedChanging);
			slider.addEventListener('changed', this._onSyncedScrollEnd);
		}
	}

	/**
	 * Called to sync the current scroll position to the synced slider
	 * in specific situations like the synced slider was newly registered,
	 * scrolling has settled or a sync pause ended.
	 * @private
	 */
	_manualSync() {
		this._syncedSlider?.moveToFraction(
			this._containerRef.value.scrollLeft / this.slideWidth,
			true,
		);
	}

	/** @private */
	_onSyncedChanging({ detail: fraction }) {
		if (
			this._syncedSettling ||
			this._syncPaused ||
			(this.isScrolling && !this._syncedStartedScroll)
		) {
			return;
		}
		if (!this.isScrolling) {
			this._syncedStartedScroll = true;
		}
		this.moveToFraction(fraction, true);
	}

	/**
	 * Scrolling starts.
	 * @private
	 */
	_onScrollStart() {
		super._onScrollStart();

		// Stop waiting for synced slider to settle scroll snap
		if (this._syncedSlider?.isScrolling) {
			this._onSyncedScrollEnd();
		}
	}

	/**
	 * Scrolling has most probably finished.
	 * Unfortunately we cannot yet use the “scrollend” event,
	 * because Apple does not yet support it.
	 * @private
	 */
	_onScrollEnd() {
		super._onScrollEnd();

		if (this._syncedSlider?.isScrolling && !this._syncedStartedScroll) {
			// Ignore synced slider and wait until it's scroll snap settles
			this._syncedSettling = true;
		}
		this._settlingFromSynced = false;
		this._syncedStartedScroll = false;
	}

	/**
	 * Scrolling in the synced slider has finished.
	 * @private
	 */
	_onSyncedScrollEnd() {
		this._syncedSettling = false;
		if (this.isScrolling && this._syncedStartedScroll) {
			// Don't sync to synced slider and wait until our scroll snap settles
			this._settlingFromSynced = true;
		}
	}

	/**
	 * Debounced call from “scroll” event.
	 * @returns {{fraction: number}}
	 * @private
	 */
	_handleScroll(isEnd = false) {
		const { fraction } = super._handleScroll(isEnd);
		if (!this._slideWidth) {
			return; // can happen when just tapping inside the container without swiping
		}
		if (
			!isEnd &&
			this._syncedSlider &&
			!this._syncedStartedScroll &&
			!this._settlingFromSynced &&
			!this._syncPaused
		) {
			this._syncedSlider.moveToFraction(fraction, true);
		}
	}

	_shouldHandleResize() {
		return super._shouldHandleResize() && !this._isSynced;
	}

	connectedCallback() {
		this._syncedSlider = null;
		this._updateSyncedSlider();
		super.connectedCallback();
	}

	updated(_changedProperties) {
		if (_changedProperties.has('sync')) {
			this._updateSyncedSlider();
		}
		super.updated(_changedProperties);
	}
}

customElements.define('flens-synchronizing-slider', FlensSynchronizingSlider);
